import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import OrganicDivider from './OrganicDivider';
import '../HomeFeaturedProduct.css';

function SpotlightCard({item, buttonLabel, showPrice, showBadge, showAction}) {
    const {product, productTitle, description, image, href} = item;
    const pricing = product.pricing || {};
    const offer = product.offer || pricing.offer || {};
    const rawPrice = offer.has_offer
        ? pricing.offer_price ?? offer.offer_price ?? pricing.selling_price ?? product.offer_price
        : pricing.selling_price ?? product.offer_price ?? product.selling_price;
    const price = rawPrice !== '' && rawPrice != null ? Number(rawPrice) : NaN;
    const mrp = Number(pricing.mrp ?? product.mrp);
    const hasPrice = Number.isFinite(price) && price >= 0;
    const hasDiscount = hasPrice && Number.isFinite(mrp) && mrp > price;
    const discount = hasDiscount ? Math.round((mrp - price) / mrp * 100) : 0;
    const badge = (Array.isArray(product.badges) ? product.badges : []).find(item => item && (item.name || item.title || item.label));
    const badgeLabel = badge ? badge.name || badge.title || badge.label : product.badge_name || product.badge || product.product_badge;
    const brand = product.brand_name || product.brand?.name;

    return <article className="nw-featured-product__spotlight" aria-label={productTitle}>
                <div className="nw-featured-product__visual">
                    <Link className="nw-featured-product__image" to={href} aria-label={`View ${productTitle}`}>
                        <img src={image} alt={productTitle} loading="lazy" onError={event => {
                            if (!event.currentTarget.dataset.fallback) {
                                event.currentTarget.dataset.fallback = 'true';
                                event.currentTarget.src = '/assets/img/logo-no-image.png';
                            }
                        }}/>
                    </Link>
                </div>
                <div className="nw-featured-product__details">
                    <div className="nw-featured-product__meta">
                        <span className="nw-featured-product__eyebrow">{brand || 'Featured pick'}</span>
                        {showBadge && typeof badgeLabel === 'string' && badgeLabel && <span className="nw-featured-product__badge">{badgeLabel}</span>}
                    </div>
                    <h3><Link to={href}>{productTitle}</Link></h3>
                    {description && <p className="nw-featured-product__description">{description}</p>}
                    {showPrice && hasPrice && <div className="nw-featured-product__pricing">
                        <strong>₹{price.toFixed(2)}</strong>
                        {hasDiscount && <del aria-label={`MRP ₹${mrp.toFixed(2)}`}>₹{mrp.toFixed(2)}</del>}
                        {showBadge && discount > 0 && <span>{discount}% off</span>}
                    </div>}
                    {showAction && <Link className="nw-featured-product__cta" to={href}><span className="nw-featured-product__cta-label">{buttonLabel}</span><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg></Link>}
                </div>
            </article>;
}

export default function FeaturedProductSpotlight({items, title, subtitle, buttonLabel, showPrice = true, showBadge = true, showAction = true, autoplay = false}) {
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 2);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [paused, setPaused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        const media = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 767px)') : null;
        const update = () => {
            setPerPage((media ? media.matches : window.innerWidth < 768) ? 1 : 2);
            setPage(0);
        };
        update();
        if (media?.addEventListener) media.addEventListener('change', update);
        else window.addEventListener('resize', update);
        return () => {
            if (media?.removeEventListener) media.removeEventListener('change', update);
            else window.removeEventListener('resize', update);
        };
    }, []);

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReducedMotion(media.matches);
        update();
        media.addEventListener?.('change', update);
        return () => media.removeEventListener?.('change', update);
    }, []);

    const pages = Math.ceil(items.length / perPage);
    const rotating = autoplay && pages > 1 && !paused && !hovered && !focused && !reducedMotion;
    useEffect(() => {
        if (!rotating) return undefined;
        const timer = window.setInterval(() => setPage(current => (current + 1) % pages), 4500);
        return () => window.clearInterval(timer);
    }, [rotating, pages]);

    if (!pages) return null;
    const activePage = page % pages;
    const start = activePage * perPage;
    const visibleItems = items.slice(start, start + perPage);

    return <section className="nw-featured-product" aria-label={title} aria-roledescription={pages > 1 ? 'carousel' : undefined}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)} onBlur={event => {if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);}}>
        <div className="container">
            <header className="nw-featured-product__heading">
                {subtitle && <p>{subtitle}</p>}
                <h2>{title}</h2>
                <OrganicDivider/>
            </header>
            <div className="nw-featured-product__grid" aria-live={rotating ? 'off' : 'polite'} aria-atomic="true">
                {visibleItems.map((item, position) => <SpotlightCard key={`${item.product.id || item.product.product_id || ''}-${start + position}`} item={item} buttonLabel={buttonLabel} showPrice={showPrice} showBadge={showBadge} showAction={showAction}/>)}
            </div>
            {pages > 1 && <div className="nw-featured-product__navigation" role="group" aria-label="Featured product navigation">
                <button type="button" className="nw-featured-product__nav" aria-label="Previous featured products" onClick={() => setPage((activePage + pages - 1) % pages)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 12H4m6-6-6 6 6 6"/></svg>
                </button>
                <div className="nw-featured-product__dots" role="group" aria-label="Choose featured product">
                    {Array.from({length:pages}, (_, position) => <button type="button" key={position} aria-label={`Show featured products page ${position + 1}`} aria-pressed={position === activePage} onClick={() => setPage(position)}/>)}
                </div>
                <span className="nw-featured-product__position" aria-live={rotating ? 'off' : 'polite'}>{start + 1}{visibleItems.length > 1 ? `–${start + visibleItems.length}` : ''} of {items.length}</span>
                <button type="button" className="nw-featured-product__nav" aria-label="Next featured products" onClick={() => setPage((activePage + 1) % pages)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg>
                </button>
                {autoplay && !reducedMotion && <button type="button" className="nw-featured-product__nav" aria-label={paused ? 'Play featured products' : 'Pause featured products'} onClick={() => setPaused(current => !current)}>
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{paused ? <path d="m9 5 11 7-11 7Z"/> : <path d="M7 5h4v14H7zm6 0h4v14h-4z"/>}</svg>
                </button>}
            </div>}
        </div>
    </section>;
}
